import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { EMPTY_ARRAY_LENGTH } from '@modeso/aumico-lib-core-fe';
import {
    ComponentType,
    GetNotesTypeTranslationsDto,
    IEquityOptions,
    IFinancialLineItemOptions,
    IFreeTableOptions,
    IInputOptions,
    IMovementScheduleEditableColumnHeaders,
    IRichText,
    ISelectOptions,
    ISuperTableOptions,
} from '@modeso/types__aumico-lib-notes-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import { cloneDeep } from 'lodash-es';

@UntilDestroy()
@Component({
    selector: 'aumico-notes-edit-notes-types-translations',
    templateUrl: './edit-notes-types-translations.component.html',
    styleUrls: ['./edit-notes-types-translations.component.scss'],
})
export class EditNotesTypesTranslationsComponent {
    @Input() currentLocale = 'de-ch';
    @Input() selectedLocale: string | undefined;
    @Input() panelOpenState = false;

    public isCompletedArray: boolean[] = [];
    public isAllCompleted: boolean = true;

    public notesTypesTranslationsList: GetNotesTypeTranslationsDto[] = [];
    @Input() set notesTypesList(value: GetNotesTypeTranslationsDto[]) {
        if (value != null) {
            this.notesTypesTranslationsList = value;
        }
    }

    private notesTypesListToBeSaved: GetNotesTypeTranslationsDto[] = [];

    @Output() translationValuesChanged: EventEmitter<GetNotesTypeTranslationsDto[]> = new EventEmitter<
        GetNotesTypeTranslationsDto[]
    >();

    constructor(private matIconRegistry: MatIconRegistry, private domSanitizer: DomSanitizer) {
        this.addSvgIcon('aumico-pattern-icon-check-circle');
        this.addSvgIcon('aumico-pattern-icon-wrong-circle');
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    public onNotesTypeTranslationsValuesChanged(notesTypeTranslation: GetNotesTypeTranslationsDto): void {
        const notesTypeIndex = this.notesTypesListToBeSaved.findIndex(
            (notesType) => notesType._id === notesTypeTranslation._id,
        );
        if (notesTypeIndex !== -1) {
            this.notesTypesListToBeSaved[notesTypeIndex] = cloneDeep(notesTypeTranslation);
        } else {
            this.notesTypesListToBeSaved.push(notesTypeTranslation);
        }
        this.translationValuesChanged.emit(this.notesTypesListToBeSaved);
        this.checkIfTranslationsForEachNoteTypesIsCompleted(this.notesTypesListToBeSaved);
    }

    private checkIfTranslationsForEachNoteTypesIsCompleted(translationResults: GetNotesTypeTranslationsDto[]): void {
        this.isAllCompleted = true;
        translationResults.forEach((translation, index) => {
            this.isCompletedArray[index] = true;
            let isTitleCompleted = true;
            let isInformationCompleted = true;
            let isComponentsCompleted = true;
            let isNoteTypeCompleted = true;

            isTitleCompleted = this.checkIfTitleTranslationIsCompleted(translation);
            isInformationCompleted = this.checkIfInformationTranslationIsCompleted(translation);
            isComponentsCompleted = this.checkIfComponentsTranslationAreCompleted(translation);

            isNoteTypeCompleted = isTitleCompleted && isInformationCompleted && isComponentsCompleted;

            this.isCompletedArray[index] = this.isCompletedArray[index] && isNoteTypeCompleted;
        });

        this.checkIfAllNotesTypesTranslationsAreCompleted();
    }

    private checkIfTitleTranslationIsCompleted(noteType: GetNotesTypeTranslationsDto): boolean {
        if (
            noteType.title != null &&
            noteType.title[this.selectedLocale] != null &&
            noteType.title[this.selectedLocale].length > EMPTY_ARRAY_LENGTH &&
            noteType.title[this.currentLocale] != null &&
            noteType.title[this.currentLocale].length > EMPTY_ARRAY_LENGTH
        ) {
            return true;
        }
        return false;
    }

    private checkIfInformationTranslationIsCompleted(noteType: GetNotesTypeTranslationsDto): boolean {
        if (
            noteType.information != null &&
            noteType.information[this.selectedLocale] != null &&
            noteType.information[this.selectedLocale].length > EMPTY_ARRAY_LENGTH &&
            noteType.information[this.currentLocale] != null &&
            noteType.information[this.currentLocale].length > EMPTY_ARRAY_LENGTH
        ) {
            return true;
        }
        return false;
    }

    private checkIfComponentsTranslationAreCompleted(notesType: GetNotesTypeTranslationsDto): boolean {
        let isComponentsTranslationsCompleted = true;

        notesType.componentsOptions.forEach((componentOption) => {
            componentOption.componentOptions.forEach((option) => {
                switch (componentOption.componentName) {
                    case ComponentType.EQUITY:
                        const equityOption = option as IEquityOptions;

                        if (
                            equityOption.textTitle[this.currentLocale] == null ||
                            equityOption.textTitle[this.currentLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            equityOption.textTitle[this.selectedLocale] == null ||
                            equityOption.textTitle[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            equityOption.currentValueTitle1[this.currentLocale] == null ||
                            equityOption.currentValueTitle1[this.currentLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            equityOption.currentValueTitle1[this.selectedLocale] == null ||
                            equityOption.currentValueTitle1[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            equityOption.currentValueTitle2[this.currentLocale] == null ||
                            equityOption.currentValueTitle2[this.currentLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            equityOption.currentValueTitle2[this.selectedLocale] == null ||
                            equityOption.currentValueTitle2[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            equityOption.previousValueTitle1[this.currentLocale] == null ||
                            equityOption.previousValueTitle1[this.currentLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            equityOption.previousValueTitle1[this.selectedLocale] == null ||
                            equityOption.previousValueTitle1[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            equityOption.previousValueTitle2[this.currentLocale] == null ||
                            equityOption.previousValueTitle2[this.currentLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            equityOption.previousValueTitle2[this.selectedLocale] == null ||
                            equityOption.previousValueTitle2[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH
                        ) {
                            isComponentsTranslationsCompleted = false;
                            return;
                        }
                        break;

                    case ComponentType.FINANCIAL_LINE_ITEM:
                        const financialLineItemOption = option as IFinancialLineItemOptions;

                        if (
                            financialLineItemOption.text[this.currentLocale] == null ||
                            financialLineItemOption.text[this.currentLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            financialLineItemOption.text[this.selectedLocale] == null ||
                            financialLineItemOption.text[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH
                        ) {
                            isComponentsTranslationsCompleted = false;
                            return;
                        }
                        break;

                    case ComponentType.MOVEMENT_SCHEDULE:
                        const movementScheduleOption = option as IMovementScheduleEditableColumnHeaders;

                        if (
                            movementScheduleOption.column1[this.currentLocale] == null ||
                            movementScheduleOption.column1[this.currentLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            movementScheduleOption.column1[this.selectedLocale] == null ||
                            movementScheduleOption.column1[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            movementScheduleOption.column2[this.currentLocale] == null ||
                            movementScheduleOption.column2[this.currentLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            movementScheduleOption.column2[this.selectedLocale] == null ||
                            movementScheduleOption.column2[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            movementScheduleOption.column3[this.currentLocale] == null ||
                            movementScheduleOption.column3[this.currentLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            movementScheduleOption.column3[this.selectedLocale] == null ||
                            movementScheduleOption.column3[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            movementScheduleOption.column4[this.currentLocale] == null ||
                            movementScheduleOption.column4[this.currentLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            movementScheduleOption.column4[this.selectedLocale] == null ||
                            movementScheduleOption.column4[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            movementScheduleOption.title?.[this.currentLocale] == null ||
                            movementScheduleOption.title?.[this.currentLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            movementScheduleOption.title?.[this.selectedLocale] == null ||
                            movementScheduleOption.title?.[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH
                        ) {
                            isComponentsTranslationsCompleted = false;
                            return;
                        }
                        break;

                    case ComponentType.SELECT:
                        const selectOption = option as ISelectOptions;

                        if (selectOption.selectionOption == null) return;
                        if (
                            selectOption.text[this.currentLocale] == null ||
                            selectOption.text[this.currentLocale].length === EMPTY_ARRAY_LENGTH ||
                            selectOption.text[this.selectedLocale] == null ||
                            selectOption.text[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            selectOption.title?.[this.currentLocale] == null ||
                            selectOption.title?.[this.currentLocale].length === EMPTY_ARRAY_LENGTH ||
                            selectOption.title?.[this.selectedLocale] == null ||
                            selectOption.title?.[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH
                        ) {
                            isComponentsTranslationsCompleted = false;
                            return;
                        }

                        selectOption.selectionOption.forEach((selectOption) => {
                            if (
                                selectOption[this.currentLocale] == null ||
                                selectOption[this.currentLocale].length === EMPTY_ARRAY_LENGTH ||
                                selectOption[this.selectedLocale] == null ||
                                selectOption[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH
                            ) {
                                isComponentsTranslationsCompleted = false;
                                return;
                            }
                        });
                        break;

                    case ComponentType.INPUT:
                        const inputOption = option as IInputOptions;

                        if (
                            inputOption.text[this.currentLocale] == null ||
                            inputOption.text[this.currentLocale].length === EMPTY_ARRAY_LENGTH ||
                            inputOption.text[this.selectedLocale] == null ||
                            inputOption.text[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH ||
                            inputOption.title?.[this.currentLocale] == null ||
                            inputOption.title?.[this.currentLocale].length === EMPTY_ARRAY_LENGTH ||
                            inputOption.title?.[this.selectedLocale] == null ||
                            inputOption.title?.[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH
                        ) {
                            isComponentsTranslationsCompleted = false;
                            return;
                        }

                        break;

                    case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                        const freeTable = option as IFreeTableOptions;

                        if (
                            freeTable.title?.[this.currentLocale] == null ||
                            freeTable.title?.[this.currentLocale].length === EMPTY_ARRAY_LENGTH ||
                            freeTable.title?.[this.selectedLocale] == null ||
                            freeTable.title?.[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH
                        ) {
                            isComponentsTranslationsCompleted = false;
                            return;
                        }

                        break;

                    case ComponentType.RICH_TEXT:
                        const richTextOption = option as IRichText;
                        if (
                            richTextOption.text == null ||
                            richTextOption.text?.length === EMPTY_ARRAY_LENGTH ||
                            richTextOption.title == null ||
                            richTextOption.title?.length === EMPTY_ARRAY_LENGTH
                        ) {
                            isComponentsTranslationsCompleted = false;
                            return;
                        }
                        break;

                    case ComponentType.SUPER_TABLE:
                        const superTableOptions = option as ISuperTableOptions;

                        if (
                            superTableOptions.title[this.currentLocale] == null ||
                            superTableOptions.title[this.currentLocale].length === EMPTY_ARRAY_LENGTH ||
                            superTableOptions.title[this.selectedLocale] == null ||
                            superTableOptions.title[this.selectedLocale]?.length === EMPTY_ARRAY_LENGTH
                        ) {
                            isComponentsTranslationsCompleted = false;
                            return;
                        }

                        break;
                }
            });
        });

        return isComponentsTranslationsCompleted;
    }

    private checkIfAllNotesTypesTranslationsAreCompleted() {
        const incompleteNotesTypeFound = this.isCompletedArray.find((value) => value === false);
        if (incompleteNotesTypeFound != null && this.isCompletedArray.length > EMPTY_ARRAY_LENGTH) {
            this.isAllCompleted = false;
        }
    }
}
